import { create } from 'zustand';
import { isIosLike, isStandaloneDisplay } from './platform.js';

interface InstallChoice {
  outcome: 'accepted' | 'dismissed';
  platform: string;
}

interface BeforeInstallPromptEvent extends Event {
  prompt: () => Promise<void>;
  userChoice: Promise<InstallChoice>;
}

interface NavigatorWithStandalone extends Navigator {
  standalone?: boolean;
}

interface PwaState {
  online: boolean;
  standalone: boolean;
  ios: boolean;
  installPrompt: BeforeInstallPromptEvent | null;
  waitingWorker: ServiceWorker | null;
  updateDismissed: boolean;
  applyingUpdate: boolean;
}

export const usePwaStore = create<PwaState>(() => ({
  online: true,
  standalone: false,
  ios: false,
  installPrompt: null,
  waitingWorker: null,
  updateDismissed: false,
  applyingUpdate: false,
}));

let initialized = false;
let registrationPromise: Promise<ServiceWorkerRegistration | null> | null = null;
let lastUpdateCheck = 0;
let reloadForUpdate = false;

export function initPwaLifecycle(): void {
  if (initialized || typeof window === 'undefined') return;
  initialized = true;

  const displayMode = window.matchMedia('(display-mode: standalone)');
  const navigatorWithStandalone = navigator as NavigatorWithStandalone;
  const refreshEnvironment = () =>
    usePwaStore.setState({
      online: navigator.onLine,
      standalone: isStandaloneDisplay(displayMode.matches, navigatorWithStandalone.standalone),
      ios: isIosLike(navigator.userAgent, navigator.platform, navigator.maxTouchPoints),
    });
  refreshEnvironment();

  window.addEventListener('online', () => {
    refreshEnvironment();
    void checkForPwaUpdate();
  });
  window.addEventListener('offline', refreshEnvironment);
  displayMode.addEventListener?.('change', refreshEnvironment);

  window.addEventListener('beforeinstallprompt', (rawEvent) => {
    const event = rawEvent as BeforeInstallPromptEvent;
    event.preventDefault();
    usePwaStore.setState({ installPrompt: event });
  });
  window.addEventListener('appinstalled', () => {
    usePwaStore.setState({ installPrompt: null, standalone: true });
  });

  if (!import.meta.env.PROD || !('serviceWorker' in navigator)) return;

  navigator.serviceWorker.addEventListener('controllerchange', () => {
    if (reloadForUpdate) window.location.reload();
  });
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'visible') void checkForPwaUpdate();
  });

  if (document.readyState === 'complete') void registerServiceWorker();
  else window.addEventListener('load', () => void registerServiceWorker(), { once: true });
}

export async function promptPwaInstall(): Promise<InstallChoice | null> {
  const event = usePwaStore.getState().installPrompt;
  if (!event) return null;
  await event.prompt();
  const choice = await event.userChoice;
  usePwaStore.setState({ installPrompt: null });
  return choice;
}

export function dismissPwaUpdate(): void {
  usePwaStore.setState({ updateDismissed: true });
}

export function applyPwaUpdate(): void {
  const worker = usePwaStore.getState().waitingWorker;
  if (!worker || reloadForUpdate) return;
  reloadForUpdate = true;
  usePwaStore.setState({ applyingUpdate: true });
  try {
    worker.postMessage({ type: 'SKIP_WAITING' });
  } catch {
    reloadForUpdate = false;
    usePwaStore.setState({ waitingWorker: null, applyingUpdate: false });
  }
}

export async function getPwaServiceWorkerRegistration(): Promise<ServiceWorkerRegistration | null> {
  if (typeof navigator === 'undefined' || !('serviceWorker' in navigator)) {
    return null;
  }
  if (!import.meta.env.PROD) {
    return (await navigator.serviceWorker.getRegistration('/')) ?? null;
  }
  return registerServiceWorker();
}

async function registerServiceWorker(): Promise<ServiceWorkerRegistration | null> {
  if (registrationPromise) return registrationPromise;
  registrationPromise = navigator.serviceWorker
    .register('/sw.js', { scope: '/' })
    .then((registration) => {
      watchRegistration(registration);
      return registration;
    })
    .catch(() => null);
  return registrationPromise;
}

function watchRegistration(registration: ServiceWorkerRegistration): void {
  if (registration.waiting && navigator.serviceWorker.controller) {
    offerUpdate(registration.waiting);
  }
  if (registration.installing) trackInstallingWorker(registration.installing, registration);
  registration.addEventListener('updatefound', () => {
    const worker = registration.installing;
    if (!worker) return;
    trackInstallingWorker(worker, registration);
  });
}

function trackInstallingWorker(
  worker: ServiceWorker,
  registration: ServiceWorkerRegistration,
): void {
  const checkState = () => {
    if (worker.state === 'installed' && navigator.serviceWorker.controller) {
      offerUpdate(registration.waiting ?? worker);
    }
  };
  checkState();
  worker.addEventListener('statechange', checkState);
}

function offerUpdate(worker: ServiceWorker): void {
  usePwaStore.setState({
    waitingWorker: worker,
    updateDismissed: false,
    applyingUpdate: false,
  });
}

async function checkForPwaUpdate(): Promise<void> {
  if (!navigator.onLine || Date.now() - lastUpdateCheck < 15 * 60 * 1_000) return;
  lastUpdateCheck = Date.now();
  const registration = await registerServiceWorker();
  try {
    await registration?.update();
  } catch {
    // A later online/focus transition retries. Connectivity UI handles the failure.
  }
}
